import React, { useId } from "react";

import { useReportEmailPreference } from "apps/company/api/rest/company-dashboard-reports";
import { useTranslation } from "lib/i18n/use-translation";

import styles from "./reports-library.module.scss";

const K = "admin:companyDashboard.reportsLibrary.emails";

const BellIcon = () => (
  <svg
    className={styles["emails-bell"]}
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

type Props = ReturnType<typeof useReportEmailPreference>;

const ReportEmailToggle = ({ preference, setEnabled, retry }: Props) => {
  const { t } = useTranslation(["admin"]);
  const labelId = useId();

  return (
    <section
      className={styles["emails"]}
      aria-busy={preference.state === "loading"}
    >
      <span id={labelId} className={styles["emails-label"]}>
        <span className={styles["emails-dot"]} aria-hidden="true" />
        <BellIcon />
        {t(`${K}.label`)}
      </span>
      {preference.state === "loading" ? (
        // Holds the switch's place, so the bar is its full height while the
        // setting loads and nothing below it moves when it arrives.
        <span className={styles["switch-placeholder"]} aria-hidden="true" />
      ) : preference.state === "error" ? (
        <div role="alert" className={styles["emails-error"]}>
          <p>{t(`${K}.loadError`)}</p>
          <button type="button" className={styles["retry"]} onClick={retry}>
            {t("admin:companyDashboard.reportsLibrary.retry")}
          </button>
        </div>
      ) : (
        // aria-disabled rather than disabled while saving, so the switch
        // keeps keyboard focus.
        <button
          type="button"
          role="switch"
          aria-checked={preference.enabled}
          aria-labelledby={labelId}
          aria-disabled={preference.saving}
          className={styles["switch"]}
          onClick={() => {
            if (!preference.saving) setEnabled(!preference.enabled);
          }}
        >
          <span className={styles["switch-thumb"]} aria-hidden="true" />
        </button>
      )}
      {preference.state === "ready" && preference.saveFailed && (
        <p role="alert" className={styles["emails-error"]}>
          {t(`${K}.saveError`)}
        </p>
      )}
    </section>
  );
};

export default ReportEmailToggle;
